/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import AnyUtil from "./AnyUtil";

export type PartitionResult<T> = T[][];

export type PartitionSelector<T> = (prev: T, curr: T) => boolean;

export type GroupByResult<T> = {
    [group: string]: T[];
};

export type GroupSelector<T> = (element: T) => string;

export default class ArrayUtil {
    public static equals<T>(array: T[], otherArray: T[], ignoreSortOrder = false, ignoreCase = false): boolean {
        if (array.length !== otherArray.length) {
            return false;
        }

        const sortedArray = [...array]
        const sortedOtherArray = [...otherArray];
        if (ignoreSortOrder) {
            sortedArray.sort();
            sortedOtherArray.sort();
        }

        for (let i = 0; i < sortedArray.length; i++) {
            if (!AnyUtil.equals(sortedArray[i], sortedOtherArray[i], ignoreCase)) {
                return false;
            }
        }

        return true;
    }

    public static isEmpty<T>(array: ReadonlyArray<T>) {
        return !array || !array.length;
    }

    public static partition<T>(elements: T[], selectPartition: PartitionSelector<T>): PartitionResult<T> {
        if (!elements || !elements.length) {
            return [[]];
        }

        let partition = 0;
        const result: T[][] = [[elements[0]]];
        const rest = elements.slice(1);
        for (const [i, el] of Array.from(rest.entries())) {
            if (selectPartition(elements[i], el)) {
                result[++partition] = [el];
            } else {
                result[partition].push(el);
            }
        }

        return result;
    }

    public static groupBy<T>(elements: T[], selectGroup: GroupSelector<T>): GroupByResult<T> {
        if (!elements) {
            return {};
        }

        return elements.reduce((acc, curr) => {
            const groupKey = selectGroup(curr);
            if (!acc[groupKey]) {
                acc[groupKey] = [curr];
            } else {
                acc[groupKey].push(curr);
            }

            return acc;
        }, {} as GroupByResult<T>);
    }

    public static groupByWithTuples<T, U>(elements: T[], selector: (element: T) => U): [U, T[]][] {
        if (elements.length < 1) return [];

        const result: [U, T[]][] = [];
        elements.forEach(element => {
            const keyCurr = selector(element);
            let group = result.find(([key, _]) => keyCurr === key);
            if (!group) {
                group = [keyCurr, []]
                result.push(group);
            }

            group[1].push(element);
        });

        return result;
    }

    public static distinctBy<T, U>(elements: T[], selector: (element: T) => U) {
        const values = elements.map(selector);
        const onlyUniques = (value: T, index: number) => values.indexOf(selector(value)) === index;
        return elements.filter(onlyUniques);
    }

    public static range(n: number): number[] {
        return Array.from(Array(n).keys());
    }

    public static zip<T, U>(arr1: T[], arr2: U[]): [T, U][] {
        return arr1.map((value, index) => [value, arr2[index]]);
    }

    public static not<T>(arr1: T[], arr2: T[]): T[] {
        return arr1.filter(value => !arr2.includes(value));
    }

    public static intersection<T>(arr1: T[], arr2: T[]): T[] {
        return arr1.filter(value => arr2.includes(value));
    }

    public static union<T>(arr1: T[], arr2: T[]): T[] {
        return arr1.concat(ArrayUtil.not(arr2, arr1));
    }

    public static splice<T>(arr: T[], start: number, deleteCount: number, ...items: T[]): T[] {
        const res = [...arr];
        res.splice(start, deleteCount, ...items);
        return res;
    }
}
